// v3.3.373 — خلية ملاحظات الحجز التراكمية الموحَّدة
// تعرض آخر ملاحظتين في العمود + كل الملاحظات عند hover + زر إضافة
import React, { useState } from "react";
import { reservations as reservationsApi } from "../services/api";
import toast from "react-hot-toast";
import { FiPlus, FiMessageSquare, FiX } from "react-icons/fi";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("ar-SA", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// v3.3.802 — قابل لإعادة الاستخدام: `api` (listNotes/addNote/deleteNote) + `title` اختياريان
// (الافتراضي = ملاحظات الحجوزات) — يُستخدم أيضًا لملاحظات الشركة على العملاء المحتملين
export default function ReservationNotesCell({ reservationId, notesLog = [], onUpdated, compact = true, api: notesApi, title }) {
  const apiRef = notesApi || reservationsApi;
  const modalTitle = title || `📝 ملاحظات الحجز #${reservationId}`;
  const [showModal, setShowModal] = useState(false);
  const [list, setList] = useState(Array.isArray(notesLog) ? notesLog : []);
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  // refresh من الـ server
  const reload = async () => {
    try {
      const r = await apiRef.listNotes(reservationId);
      setList(r.data?.notes?.map(n => ({ id: n.id, text: n.note_text, user: n.user_name, date: n.created_at })) || []);
    } catch {}
  };

  const handleAdd = async () => {
    const t = text.trim();
    if (!t) return;
    setSaving(true);
    try {
      await apiRef.addNote(reservationId, t);
      setText("");
      setAdding(false);
      await reload();
      onUpdated && onUpdated();
      toast.success("تمت إضافة الملاحظة");
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل الإضافة");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm("حذف هذه الملاحظة؟")) return;
    try {
      await apiRef.deleteNote(reservationId, noteId);
      await reload();
      onUpdated && onUpdated();
      toast.success("تم الحذف");
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل الحذف");
    }
  };

  // آخر ملاحظتين للعرض المختصر
  const last2 = list.slice(0, 2);

  return (
    <>
      <div
        onClick={(e) => { e.stopPropagation(); setShowModal(true); reload(); }}
        title={list.length > 0 ? list.map(n => `[${fmtDate(n.date)}] ${n.user || "—"}: ${n.text}`).join("\n\n") : "اضغط لإضافة ملاحظة"}
        style={{
          cursor: "pointer",
          minHeight: 28,
          maxWidth: compact ? 220 : 320,
          padding: "4px 6px",
          fontSize: 11,
          lineHeight: 1.5,
          color: "#475569",
        }}
      >
        {list.length === 0 ? (
          <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>+ إضافة ملاحظة</span>
        ) : (
          <>
            {last2.map((n, i) => (
              <div key={n.id || i} style={{ marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                <span style={{ color: "#94a3b8", fontSize: 9 }}>
                  {fmtDate(n.date).split(" ")[0]} • {n.user?.split(" ")[0] || "—"}:
                </span>{" "}
                <span style={{ color: "#1e293b" }}>{n.text}</span>
              </div>
            ))}
            {list.length > 2 && (
              <div style={{ color: "#2563eb", fontSize: 9, fontWeight: 600 }}>
                + {list.length - 2} ملاحظات أخرى (اضغط لعرض الكل)
              </div>
            )}
          </>
        )}
      </div>

      {showModal && (
        <div onClick={() => setShowModal(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 560, maxHeight: "85vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 20px 50px rgba(0,0,0,0.3)" }}>

            {/* Header */}
            <div style={{ background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)", color: "#fff", padding: "12px 16px", display: "flex", alignItems: "center", gap: 10 }}>
              <FiMessageSquare size={18} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{modalTitle}</div>
                <div style={{ fontSize: 11, opacity: 0.9 }}>{list.length} ملاحظة • تتراكم ولا تُحذف تلقائياً</div>
              </div>
              <button onClick={() => setShowModal(false)}
                style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 6, padding: 6, cursor: "pointer", color: "#fff" }}>
                <FiX size={16} />
              </button>
            </div>

            {/* Notes list */}
            <div style={{ flex: 1, overflowY: "auto", padding: 12, background: "#f8fafc" }}>
              {list.length === 0 ? (
                <div style={{ textAlign: "center", color: "#94a3b8", padding: 30, fontSize: 13 }}>
                  لا توجد ملاحظات بعد. أضف أول ملاحظة من الأسفل.
                </div>
              ) : (
                list.map((n, i) => (
                  <div key={n.id || i} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 10, marginBottom: 8, position: "relative" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 10, color: "#64748b" }}>
                      <span style={{ fontWeight: 600, color: "#2563eb" }}>👤 {n.user || "—"}</span>
                      <span>📅 {fmtDate(n.date)}</span>
                    </div>
                    <div style={{ fontSize: 13, color: "#1e293b", lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {n.text}
                    </div>
                    <button onClick={() => handleDelete(n.id)}
                      title="حذف (للمنشئ أو الأدمن فقط)"
                      style={{ position: "absolute", top: 6, left: 6, background: "transparent", border: "none", color: "#cbd5e1", cursor: "pointer", padding: 2 }}
                      onMouseEnter={e => e.currentTarget.style.color = "#dc2626"}
                      onMouseLeave={e => e.currentTarget.style.color = "#cbd5e1"}>
                      <FiX size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Composer */}
            <div style={{ borderTop: "1px solid #e2e8f0", padding: 12, background: "#fff" }}>
              {adding ? (
                <>
                  <textarea value={text} onChange={e => setText(e.target.value)}
                    placeholder="اكتب ملاحظة جديدة..."
                    rows={3}
                    autoFocus
                    style={{ width: "100%", padding: 10, border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }} />
                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    <button onClick={handleAdd} disabled={saving || !text.trim()}
                      style={{ padding: "7px 14px", background: text.trim() && !saving ? "#16a34a" : "#cbd5e1", color: "#fff", border: "none", borderRadius: 6, cursor: text.trim() && !saving ? "pointer" : "not-allowed", fontSize: 12, fontFamily: "inherit", fontWeight: 600 }}>
                      {saving ? "جاري الحفظ..." : "💾 حفظ الملاحظة"}
                    </button>
                    <button onClick={() => { setAdding(false); setText(""); }}
                      style={{ padding: "7px 14px", background: "#f1f5f9", color: "#64748b", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>
                      إلغاء
                    </button>
                  </div>
                </>
              ) : (
                <button onClick={() => setAdding(true)}
                  style={{ width: "100%", padding: "8px 14px", background: "#dbeafe", color: "#1e40af", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontFamily: "inherit", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                  <FiPlus size={14} /> إضافة ملاحظة جديدة
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
